import { useMemo } from 'react'
import { mergeRefreshedRows } from '../../helpers/tables/merge-refreshed-rows'
import Strapi, { EndpointQueryParameters, StandardStrapiFields } from '../../types/strapi/strapi'

const ROWS_REFRESH_POLLING_INTERVAL_MS = 60_000

interface RowsRefreshQueryOptions {
    skip: boolean
    pollingInterval: number
    skipPollingIfUnfocused: boolean
    refetchOnFocus: boolean
    refetchOnReconnect: boolean
}

interface UseRowsRefreshParams<T> {
    rows: T[]
    useQuery: (
        params: EndpointQueryParameters,
        options: RowsRefreshQueryOptions
    ) => { currentData?: Strapi<T> }
    /** Поля строки, которые нужно обновлять; без них обновляются все скалярные поля */
    fields?: EndpointQueryParameters['fields']
    /** Связи строки, которые нужно обновлять; каждая заменяется целиком */
    populate?: EndpointQueryParameters['populate']
}

/**
 * Опрашивает свежие данные строк, уже показанных в таблице, по их documentId.
 * Состав и порядок строк не меняются: новые записи не подтягиваются, пропавшие не убираются.
 */
export default function useRowsRefresh<T extends Pick<StandardStrapiFields, 'documentId'>>({
    rows,
    useQuery,
    fields,
    populate,
}: UseRowsRefreshParams<T>): T[] {
    const idsKey = rows.map(row => row.documentId).join(',')

    const params = useMemo(() => {
        const ids = idsKey.split(',')

        return {
            filters: { documentId: { $in: ids } },
            fields,
            populate,
            pagination: { page: 1, pageSize: ids.length },
        }
    }, [idsKey, fields, populate])

    const { currentData } = useQuery(params, {
        skip: !idsKey,
        pollingInterval: ROWS_REFRESH_POLLING_INTERVAL_MS,
        skipPollingIfUnfocused: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    })

    return useMemo(
        () => mergeRefreshedRows(rows, currentData?.data ?? []),
        [rows, currentData]
    )
}
